import { Logger } from "@devforce/trailhead-client-logger";

/**
 * Utility function to create and manage an IntersectionObserver.
 * * @param {HTMLElement} targetElement - The DOM element to observe (usually this.template.host).
 * @param {Function} onVisibleCallback - The function to execute when the element becomes visible.
 * @param {Object} options - Observer options (e.g., { root: null, threshold: 0.1 }).
 * @returns {IntersectionObserver} The created observer instance.
 */
export function createVisibilityObserver(
  targetElement: HTMLElement,
  onVisibleCallback: () => void,
  options: IntersectionObserverInit = {}
) {
  if (!targetElement) {
    const logger = new Logger({
      prefix: "[th-compoments][visibilityHelper:createVisibilityObserver] ",
    });
    logger.error("Intersection Observer: Target element is null or undefined.");
    return null;
  }

  const defaultOptions = {
    root: null,
    rootMargin: "0px",
    threshold: 0.1, // 10% of the target is visible
  };

  const observer = new IntersectionObserver(
    (entries, observerInstance) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          onVisibleCallback();

          observerInstance.unobserve(entry.target);
        }
      });
    },
    { ...defaultOptions, ...options }
  );

  observer.observe(targetElement);

  return observer;
}

/**
 * Utility function to clean up the observer.
 * * @param {IntersectionObserver} observer - The observer instance to disconnect.
 */
export function disconnectObserver(observer: IntersectionObserver | null) {
  if (observer) {
    observer.disconnect();
  }
}
